import React, { useMemo, useState } from 'react'
import {
  BotMitigationSliderWrapper,
  BotMitigationStyled,
  BotMitigationSliderText,
} from 'Features/pricing/tabs/BotMitigation/BotMitigation.styled'
import { PricingTabHeader } from 'Features/pricing/components/PricingTabHeader/PricingTabHeader'
import { GeneralSelect, Globe, Slider, Toast } from '@clickcease/cc-client-components-lib'
import { useTranslation } from 'react-i18next'
import { Plan } from 'Features/pricing/types/Plan'
import { useSelectPlan } from 'Features/pricing/hooks/useSelectPlan'
import { PlanFeaturesTable } from 'Features/pricing/components/PlanFeaturesTable/PlanFeaturesTable'
import { BotMitigationTableData } from 'Features/pricing/tabs/BotMitigation/BotMitigationTableData'
import { isMobile } from 'react-device-detect'
import plansJson from 'Features/pricing/tabs/BotMitigation/botMitigationPlans.json'
import { PricingPlansDesktop } from 'Features/pricing/components/PricingPlansDesktop/PricingPlansDesktop'
import { PricingPlansMobile } from '../PaidMarketing/PricingPlansMobile/PricingPlansMobile'
import {
  PaidMarketingSelectWrapper,
  ToasterWrapper,
} from 'Features/pricing/tabs/PaidMarketing/PaidMarketing.styled'
import Currencies from 'Assets/lists/currencies'
import { SelectOption } from 'Types/SelectOption'
import { PRODUCT_TYPES } from 'Types/ProductTypes'
import { CURRENCIES, CurrenciesTypes } from 'Types/Currencies'
import { botMitigationPlanSizes } from 'Features/pricing/models/planSizes'
import { wrapperCss, wrapperMobileCss } from '../helper'
import { useVerifyUrlCodeFromParams } from 'Features/auth/hooks/useVerifyUrlCodeFromParams'

type BotMitigationProps = {
  plansDb: Array<Plan>
  partner?: boolean
  reactivation?: boolean
  billingCycleMonths?: number
  source: string
}

export const BotMitigation = ({
  plansDb,
  partner,
  reactivation,
  billingCycleMonths,
  source,
}: BotMitigationProps) => {
  const { t } = useTranslation('pricing')
  const [numOfVisits, setNumOfVisits] = useState<number | number[]>(200)
  const [currency, setCurrency] = useState<CurrenciesTypes>(CURRENCIES.USD)
  const planSelected = useSelectPlan(plansDb, PRODUCT_TYPES.BOT_ZAPPING, currency)
  const { getSessionStorageItem } = useVerifyUrlCodeFromParams()
  const renderCoupon = getSessionStorageItem('cpncd')

  const plansDbFiltered = useMemo(() => {
    const plans: Array<Plan> = []
    plansDb.forEach((plan) => {
      if (plan.currency === currency) {
        plans.push(plan)
      }
    })
    return plans
  }, [plansDb, currency])

  return (
    <BotMitigationStyled>
      <PricingTabHeader
        icon={isMobile ? undefined : <Globe />}
        text={t('bot_mitigation.header_title')}
      />
      <BotMitigationSliderWrapper>
        <BotMitigationSliderText>
          {t('bot_mitigation.yearly_visits_to_your_website')}
        </BotMitigationSliderText>
        <Slider
          isMobile={isMobile}
          css={{}}
          width={isMobile ? '94%' : '500px'}
          min={200}
          max={1750}
          value={100}
          steps={botMitigationPlanSizes}
          minUnit={'k'}
          maxUnit={t('bot_mitigation.million')}
          maxText={t('bot_mitigation.million')}
          onChange={(value) => setNumOfVisits(value)}
        />
        <PaidMarketingSelectWrapper>
          <GeneralSelect
            value={Currencies.find((item) => item.value === currency)}
            options={Currencies}
            wrapperCSS={isMobile ? wrapperMobileCss : wrapperCss}
            onChange={(item: SelectOption) => {
              setCurrency(item?.value as CurrenciesTypes)
            }}
          />
        </PaidMarketingSelectWrapper>
      </BotMitigationSliderWrapper>
      {(renderCoupon || partner) && (
        <ToasterWrapper>
          <Toast type="info">{t('toaster_text')}</Toast>
        </ToasterWrapper>
      )}
      {isMobile ? (
        <PricingPlansMobile
          numOfVisits={numOfVisits as number}
          plansDb={plansDbFiltered}
          currency={currency}
          plansJson={plansJson}
          productName={PRODUCT_TYPES.BOT_ZAPPING}
          reactivation={reactivation}
          billingCycleMonths={billingCycleMonths}
          source={source}
        />
      ) : (
        <PricingPlansDesktop
          numOfVisits={numOfVisits as number}
          plansDb={plansDbFiltered}
          plansJson={plansJson}
          currency={currency}
          productName={PRODUCT_TYPES.BOT_ZAPPING}
          reactivation={reactivation}
          billingCycleMonths={billingCycleMonths}
          source={source}
        />
      )}
      <PlanFeaturesTable
        data={BotMitigationTableData}
        exceededMaxVisits={(numOfVisits as number) > plansJson.max_yearly_visits}
        reactivation={reactivation}
        selectPlan={(type) =>
          planSelected.planSelected({
            type,
            numOfVisits: numOfVisits as number,
            reactivation,
            billingCycleMonths,
          })
        }
        source={source}
      />
    </BotMitigationStyled>
  )
}
